import type { ReactNode } from 'react'
import { QueryErrorBanner, QueryErrorMessage } from './QueryErrorBanner'

/** Error side of a table's query: what failed and how to try again. */
export interface QueryErrorState {
  /** The query's error (its raw message is shown, ADR 0006), or null when it is fine. */
  error: Error | null
  /** A refetch is in flight (TanStack `isFetching`): Retry is disabled and says "Retrying…". */
  retrying: boolean
  /** Refetch of the failed query, offered with the error. */
  onRetry: () => void
}

/**
 * Query state every presentational table component takes besides its rows,
 * mapped from TanStack Query by the owner of the query.
 */
export interface TableQueryStateProps extends QueryErrorState {
  /** No data yet (TanStack `isPending` — not `isLoading`, which is false while the query is disabled). */
  loading: boolean
  /** Rows on screen are placeholder data of a previous key (TanStack `isPlaceholderData`): dimmed and inert. */
  refreshing: boolean
}

/** The slice of a TanStack Query result a table's state is derived from. */
export interface TableQuerySource {
  isPending: boolean
  isPlaceholderData: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => unknown
}

/**
 * The one mapping from a query result to `TableQueryStateProps`. Owners of a
 * query call this instead of mapping the flags by hand, so every table treats
 * the same query state the same way:
 * - `loading` while there is nothing to show — no data yet, or placeholder
 *   data of a previous key that holds no rows (dimming an empty state would
 *   flash "No … found" under a working Clear Filters button);
 * - `refreshing` while rows of a previous key are on screen.
 * A table fed by several queries (page + count) passes one combined source:
 * `error` through `pageOrCountError`, `isFetching` of either, `refetch` of both.
 */
export function tableQueryState(query: TableQuerySource, rowCount: number): TableQueryStateProps {
  return {
    loading: query.isPending || (query.isPlaceholderData && rowCount === 0),
    refreshing: query.isPlaceholderData && rowCount > 0,
    error: query.error,
    retrying: query.isFetching,
    onRetry: () => {
      void query.refetch()
    },
  }
}

/**
 * The error of a server-paged table whose total comes from a separate count
 * query: the page's own error, else the count's. Without the count the footer
 * is gone while the rows still render, so a failed count is an error of the
 * table too — named as the total, so it is not read as the rows failing.
 */
export function pageOrCountError(pageError: Error | null, countError: Error | null): Error | null {
  if (pageError) return pageError
  if (!countError) return null
  // A bodiless failure (a HEAD count answering 500) carries an empty message.
  const detail = countError.message ? `: ${countError.message}` : ''
  return new Error(`Couldn't load the total${detail}`)
}

/**
 * Where a query error goes for a table. Policy: a failed query never replaces
 * rows already on screen (TanStack keeps `data` when a refetch fails) — with
 * rows it is a banner above the table, only with no rows does it take their
 * place inside the table. Callers render `banner` and the DataTable as
 * siblings; the banner has no margin, so the parent's `space-y-*` spaces them.
 *
 * @param query - The query's error state; `retrying` disables Retry in both places.
 * @param rowCount - Rows the table is about to render.
 * @returns `banner` to render above the table and `tableError` for DataTable `error`.
 */
export function queryErrorDisplay(
  { error, retrying, onRetry }: QueryErrorState,
  rowCount: number,
): { banner: ReactNode; tableError: ReactNode } {
  if (!error) return { banner: null, tableError: undefined }
  // A bodiless failure (e.g. a HEAD count answering 500) carries an empty message.
  const message = error.message || 'Failed to load'
  if (rowCount > 0) {
    return {
      banner: <QueryErrorBanner message={message} onRetry={onRetry} retrying={retrying} />,
      tableError: undefined,
    }
  }
  return {
    banner: null,
    tableError: <QueryErrorMessage message={message} onRetry={onRetry} retrying={retrying} />,
  }
}
